/**
 * @module views
 *
 * All the things the user can feel with their senses:
 *
 * - things you can see (like data and graphics)
 * - things you can touch (like buttons and inputs)
 * - things you can hear (like with a screen reader)
 * - things you can request (possible ui events)
 *
 * The root views module exports a single React component which is the app's
 * user interface. This is effectively the root UI layout which composes the
 * whole rest of the app from the components in this folder.
 *
 * All the files within this folder are React components^. They are divided into
 * a small set of types. The two main types are: "pages" and "sections".
 *
 * ^ the only folders that aren't actual React components are the "shared" folders
 * since they are more of a "library" of components, not components themselves.
 *
 * -- MyComponent.page -----
 * | index                 |
 * | MyComponent.layout    |
 * | HelperComponent       |
 * -------------------------
 *
 * Pages are the starting point for the app. They have a 1 to 1 mapping to a
 * route endpoint and as such can potentially be the first thing a user loads
 * in their browser and starts the app. These components are usually connected
 * to the app's state, and (in the case that they are folders) pass down props
 * to their child components.
 *
 * Pages are either a single file, if they aren't too complex, or a folder of
 * related components, if they require being broken down and composed together.
 * The "layout" file is the main component that "lays out" the page and
 * composes it, making use of all the other helper/child components it has
 * available to it (including other shared components).
 *
 * -- MyComponent.section --
 * | index                 |
 * | MyComponent.layout    |
 * | MyComponent.routes    |
 * | MyComponent.shared    |
 * | First.page            |
 * | Second.page           |
 * | Another.section       |
 * -------------------------
 *
 * Sections are collections of pages and other sections. Each section has not
 * only a "layout" (like a page) but also a "routes" component which *only*
 * contains the routing information mapping url endpoints to page (or section)
 * components.
 *
 * Routes are a good place to parse incoming parameters coming from the routes and
 * pass them into page (or section) components as props rather than requiring
 * individual pages or sections to be concerned about the shape of the routes
 * and their parameters. This helps keep the concerns isolated and is actually
 * easier to reason about since the endpoint that defines the name of a param
 * will be right beside the code that parses that param name.
 *
 * The thing to remember is this hierarchy:
 *
 * 1. a section (and you can think of the root `/views` folder as a "section")
 *    exports a layout component (and the export should itself *be* a React
 *    component by default)
 * 2. a layout composes a section (or page) and wraps its routes with generic
 *    containers/markup/styles (for that section or page)
 * 3. a routes file defines endpoints for a section and connects them to a
 *    page or another section within this section
 * 4. a page can be a folder with its own hierarchy of child components
 * 5. sections can contain other sections (the whole pattern is recursive)
 * 6. any component used by more than one page or section should be stored in
 *    that section's shared folder (or a parent folder if other sibling sections
 *    also want to use it). NOTE: share the component at the lowest, most
 *    specific, level possible so as to encapsulate as much detail as possible
 *    thereby making each given level much easier (for humans) to comprehend.
 *
 * -- MyComponent.shared ---
 * | index                 |
 * | SharedComponentOne    |
 * | SharedComponentTwo    |
 * | SharedComponentThree  |
 * -------------------------
 *
 * Sections also can (optionally) include a special "shared" folder. This folder
 * is (unlike every other file or folder within the "views") a module which acts
 * as a collection of re-usable components. Each component in this folder should
 * be generic enough that it does not depend on any external state and is
 * completely self-contained through its props as its only interface. These
 * are gathered through the index and an interface to import from this
 * collection is exposed, but the "shared" folder itself is not a React
 * component.
 *
 */
import UI from './UI.layout';

export default UI;
